import { ArrowRight } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Badge } from '../atoms/Badge'
import { StatusDot } from '../atoms/StatusDot'
import { Card } from '../card'

/**
 * FlowCard — system pipeline card on the unified flows-list page.
 *
 * Two variants: "login" (green accent) and "register" (amber accent).
 * Renders the pipeline name, published/draft status pill, description,
 * a stats footer, and an "Open editor" CTA whose colour inherits the
 * card's accent token.
 *
 * The entire card surface is clickable and keyboard-activatable (Enter
 * or Space). A 2px accent stripe runs across the top exactly as in the
 * Phase 2 mockup (`.flow-card::before` pattern).
 *
 * Composes: Badge (atom), StatusDot (atom).
 */

export type FlowCardAccent = 'login' | 'register'

export interface FlowCardProps {
  /** Pipeline display name. */
  name: string
  /** Short description shown below the name + pill row. */
  description: string
  /** Whether the pipeline is currently published. */
  published: boolean
  /** Number of hook stages. */
  stagesCount: number
  /** Number of configured actions. */
  actionsCount: number
  /** Number of system blocks in the DAG. */
  systemBlocksCount: number
  /**
   * ISO 8601 string or Date for relative "edited N ago" label.
   * Pass a string that's already humanised (e.g. "4h ago") if you
   * prefer to format on the caller side.
   */
  editedAt: string | Date
  /** Accent token — "login" → green, "register" → amber. */
  accent: FlowCardAccent
  /** Invoked when the card or its CTA is activated. */
  onClick: () => void
  /** Extra classes for the outer wrapper. */
  className?: string
  /**
   * Override "now" for deterministic test / story output.
   * Only used when `editedAt` is a Date instance.
   */
  nowFn?: () => Date
}

/**
 * formatEditedAt — converts a Date to a concise "Xh ago" / "just now" label.
 * When editedAt is a plain string it is returned as-is (pre-formatted path).
 */
function formatEditedAt(editedAt: string | Date, now: Date): string {
  if (typeof editedAt === 'string') return editedAt
  const diffMs = now.getTime() - editedAt.getTime()
  if (diffMs < 60_000) return 'just now'
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(diffMs / 3_600_000)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(diffMs / 86_400_000)
  return `${days}d ago`
}

// Token mapping for the two accent modes.
// We use inline CSS custom property injection so the accent colour is a single
// source of truth: the top stripe, the CTA text, and the "Open editor" arrow
// all pull from `--fc-accent`.
const ACCENT_CLASSES: Record<FlowCardAccent, { stripe: string; cta: string; pill: string }> = {
  login: {
    stripe: 'before:bg-ia-success',
    cta: 'text-ia-success',
    pill: 'success',
  },
  register: {
    stripe: 'before:bg-ia-warning',
    cta: 'text-ia-warning',
    pill: 'warning',
  },
}

export const FlowCard = React.forwardRef<HTMLDivElement, FlowCardProps>(
  (
    {
      name,
      description,
      published,
      stagesCount,
      actionsCount,
      systemBlocksCount,
      editedAt,
      accent,
      onClick,
      className,
      nowFn,
    },
    ref,
  ) => {
    const acc = ACCENT_CLASSES[accent]
    const now = nowFn ? nowFn() : new Date()
    const editedLabel = formatEditedAt(editedAt, now)

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      },
      [onClick],
    )

    return (
      <Card
        ref={ref}
        role="article"
        aria-label={`${name} pipeline, ${published ? 'published' : 'draft'}`}
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        className={cn(
          // Base card surface — matches mockup bg-card, border-card
          'relative overflow-hidden rounded-[10px]',
          'p-[22px_24px_20px_24px]',
          'cursor-pointer select-none',
          // Transition: border-color + lift
          'transition-[border-color,transform] duration-150',
          'hover:border-ia-border-strong hover:-translate-y-px',
          // Focus ring
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          // Accent stripe via ::before (2px top strip)
          'before:absolute before:inset-x-0 before:top-0 before:h-[2px] before:opacity-70',
          acc.stripe,
          className,
        )}
      >
        {/* Head row: name + status pill */}
        <div className="flex items-center gap-3 mb-3">
          <h3 className="text-[17px] font-medium leading-snug tracking-[-0.005em] text-ia-text">
            {name}
          </h3>
          <Badge
            variant={published ? 'outlined-success' : 'outlined'}
            size="sm"
            aria-label={published ? 'Published' : 'Draft'}
            className="font-mono"
          >
            <StatusDot status={published ? 'active' : 'pending'} size="sm" aria-hidden="true" />
            {published ? 'Published' : 'Draft'}
          </Badge>
        </div>

        {/* Description */}
        <p className="text-[13px] leading-[1.55] text-ia-text-secondary">{description}</p>

        {/* Stats footer */}
        <div
          className={cn(
            'flex items-center gap-4 flex-wrap mt-4 pt-[14px]',
            'border-t border-dashed border-ia-border',
            'font-mono text-[11px] text-ia-text-muted',
          )}
        >
          <span className="inline-flex gap-1.5 items-baseline">
            <span className="text-ia-text-secondary font-medium tabular-nums">{stagesCount}</span>
            <span>stages</span>
          </span>
          <span className="inline-flex gap-1.5 items-baseline">
            <span className="text-ia-text-secondary font-medium tabular-nums">{actionsCount}</span>
            <span>actions</span>
          </span>
          <span className="inline-flex gap-1.5 items-baseline">
            <span className="text-ia-text-secondary font-medium tabular-nums">
              {systemBlocksCount}
            </span>
            <span>system blocks</span>
          </span>
          <span className="ml-auto text-ia-text-muted">{`edited ${editedLabel}`}</span>
        </div>

        {/* Open editor CTA */}
        <div
          className={cn(
            'mt-[18px] inline-flex items-center gap-1.5',
            'font-mono text-[11.5px]',
            acc.cta,
          )}
          aria-hidden="true"
        >
          Open editor
          <ArrowRight size={11} strokeWidth={2} />
        </div>
      </Card>
    )
  },
)
FlowCard.displayName = 'FlowCard'
